import { $Types } from '@Ravnur-Inc/core/typings/tsx';
import { Prop, Watch } from '@Ravnur-Inc/decorators';
import find from '@Ravnur-Inc/nanoutils/find';
import { renderSlot } from 'vue';
import { Vue, Options } from 'vue-class-component';

import EditEntityComponent from '@/components/edit-entity-component/edit-entity-component';
import EntityLoader from '@/components/entity-loader/entity-loader';
import KeyValueSummary from '@/components/key-value-summary/key-value-summary';
import PermissionGuard from '@/components/permission-guard/permission-guard';
import WithHierarchicalBreadcrumbs from '@/components/with-hierarchical-breadcrumbs/with-hierarchical-breadcrumbs';
import { UNSAVED_REGISTRY_CTX } from '@/constants';
import { Details$TabComponent, BaseEditorComponent } from '@/types/Component';
import { KeyValueEntry } from '@/types/KeyValueEntry';
import { IRepository } from '@/types/Repository';
import { UnsavedCheckerContext } from '@/types/UnsavedChecker';

import './base-details-page.scss';

export enum Details$ComponentType {
  EDITOR = 'editor',
  OTHER = 'other',
}

type Details$BaseTabInfo = {
  id: string;
  label: string;
};

type Details$EditorTabInfo<E extends Entity> = {
  type: Details$ComponentType.EDITOR;
  editor: new () => BaseEditorComponent<E>;
} & Details$BaseTabInfo;

type Details$OtherTabInfo<E extends Entity> = {
  type: Details$ComponentType.OTHER;
  component: Details$TabComponent<E>;
} & Details$BaseTabInfo;

export type Details$TabInfo<E extends Entity> = Details$EditorTabInfo<E> | Details$OtherTabInfo<E>;

type Props<E extends Entity> = {
  repository: IRepository<E>;
  titleKey: keyof E;
  tabs: Array<Details$TabInfo<E>>;
  summary?: KeyValueEntry[];
  wideTabs?: boolean;
};

type Emits<E> = {
  onRefresh: (entity: E) => void;
};

const CN = 'base-details-page';

@Options({
  emits: ['refresh'],
  inject: {
    unsavedCheckerCtx: UNSAVED_REGISTRY_CTX,
  },
})
export default class BaseDetailsPage<E extends Entity> extends Vue {
  declare $props: $Types<Props<E>, Emits<E>>;
  declare $refs: {
    loader: EntityLoader<E>;
    editor: EditEntityComponent<E> | undefined;
  };
  declare unsavedCheckerCtx: UnsavedCheckerContext;

  @Prop({ type: Object, required: true })
  public readonly repository!: IRepository<E>;
  @Prop({ type: String, required: true })
  public readonly titleKey!: keyof E;
  @Prop({ type: Array, required: true })
  public readonly tabs!: Array<Details$TabInfo<E>>;
  @Prop({ type: Array, default: () => [] })
  public readonly summary!: KeyValueEntry[];
  @Prop({ type: Boolean, default: false })
  public readonly wideTabs!: boolean;

  private entity: Nullable<E> = null;
  private tab: Nullable<Details$TabInfo<E>> = null;

  private get title(): string {
    const val = this.entity?.[this.titleKey];
    return typeof val === 'string' ? val : '';
  }

  public reload() {
    this.$refs.loader.load();
  }

  @Watch('tabs', { immediate: true })
  protected handleTabsChanged() {
    const { tab, tabs } = this;
    const selectedTab = tab || this.tabs.find((t) => t.id === this.$route.query.tab);
    const t = selectedTab ? find((t1: Details$TabInfo<E>) => t1.id === selectedTab.id, tabs) : null;
    this.tab = t || this.tabs[0];
  }

  protected refresh(entity: E) {
    this.entity = entity;
    this.$emit('refresh', entity);
  }

  public hasUnsavedData() {
    return this.$refs.editor?.isChanged || false;
  }

  created() {
    this.unsavedCheckerCtx.registry(this.hasUnsavedData);
  }

  beforeUnmount() {
    this.unsavedCheckerCtx.unregistry(this.hasUnsavedData);
  }

  render() {
    const cn = {
      [CN]: true,
      [`${CN}--wide`]: this.wideTabs,
    };

    const slots = {
      header: this.$slots.header,
    };

    return (
      <card class={cn} title={this.title} v-slots={slots}>
        <PermissionGuard />
        <WithHierarchicalBreadcrumbs leafTitle={this.title} />
        <div class={`${CN}__main`}>
          {renderSlot(this.$slots, 'pre-main')}
          <KeyValueSummary list={this.summary} />
          {renderSlot(this.$slots, 'after-main')}
        </div>
        <div class={`${CN}__content`}>
          <EntityLoader ref="loader" repository={this.repository} onRefresh={this.refresh} />
          {this.entity ? this.renderTabs() : <spinner />}
        </div>
      </card>
    );
  }

  private renderTabs() {
    const slots = {
      tab: (props: { tab: Details$TabInfo<E> }) => props.tab.label,
    };
    return (
      <tabs tabs={this.tabs} v-slots={slots} value={this.tab} onInput={this.moveToTab}>
        {this.tab ? this.renderTabContent(this.tab) : null}
      </tabs>
    );
  }

  private renderTabContent(tab: Details$TabInfo<E>) {
    const { entity } = this;
    if (!entity) {
      return null;
    }

    if (tab.type === Details$ComponentType.EDITOR) {
      const editor = tab.editor;
      return (
        <EditEntityComponent
          key={tab.id}
          ref="editor"
          editor={editor}
          originEntity={entity}
          repository={this.repository}
          onStored={this.refresh}
        />
      );
    }
    const Comp = tab.component;
    return <Comp origin={entity} onStored={this.refresh} />;
  }

  private async moveToTab(tab: Details$TabInfo<E>) {
    try {
      this.$router.push({ query: { tab: tab.id } }).then(() => {
        this.tab = tab;
      });
    } catch (e) {
      // noop
    }
  }
}
